import React, { useEffect, useState } from 'react'
import { Box, Typography, LinearProgress } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { TaskProgressResponse, SubTask } from '../../types'

interface DocumentExtractionProgressProps {
    taskProgress?: TaskProgressResponse
    isLoading?: boolean
    error?: Error | null
    hasFailed?: boolean
    failureMessage?: string | null
}

type ProgressStep = {
    label: string
    current: number
    total: number
    status?: string
    elapsed?: number
    remaining?: number
}

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        padding: theme.spacing(3),
    },
    failureWrapper: {
        padding: theme.spacing(3),
        display: 'flex',
        justifyContent: 'center',
    },
    failureCard: {
        border: `1px solid ${theme.palette.common.black}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(3),
        maxWidth: 560,
        width: '100%',
        textAlign: 'center',
    },
    failureText: {
        color: theme.palette.common.black,
    },
    message: {
        marginBottom: theme.spacing(2),
    },
    stepSection: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
    },
    stepHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
    },
    progressBar: {
        height: 8,
        borderRadius: 4,
    },
}))

const formatTime = (seconds: number | undefined): string => {
    if (seconds === undefined || seconds === null) return ''

    if (seconds < 60) {
        return `${Math.round(seconds)}s`
    }

    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)

    if (hours > 0) {
        const remainingMinutes = minutes % 60
        if (remainingMinutes > 0) {
            return `${hours}hr ${remainingMinutes}m`
        }
        return `${hours}hr`
    }

    return `${minutes}m`
}

const getStepFromSubTask = (
    label: string,
    subTask: SubTask | undefined,
): ProgressStep | null => {
    if (!subTask) return null

    const current = subTask.results.currently_on_record ?? 0
    const total = subTask.results.total_records ?? 0
    const status =
        subTask.results.sub_task_message ??
        subTask.results.sub_task_status ??
        undefined

    return {
        label,
        current,
        total,
        status,
        elapsed: subTask.results.elapsed_seconds ?? undefined,
        remaining: subTask.results.estimated_remaining_seconds ?? undefined,
    }
}

export const DocumentExtractionProgress: React.FC<DocumentExtractionProgressProps> = ({
    taskProgress,
    isLoading = false,
    error = null,
    hasFailed = false,
    failureMessage = null,
}) => {
    const classes = useStyles()

    const extractionSubTask = taskProgress?.sub_tasks?.find(
        (subTask) => subTask.sub_task_type === 'DATA_EXTRACTION',
    )

    const progressSteps = [
        getStepFromSubTask('Data Extraction', extractionSubTask),
    ].filter((step): step is ProgressStep => step !== null)

    const [lastProgress, setLastProgress] = useState<ProgressStep[]>([])

    useEffect(() => {
        if (progressSteps.length > 0) {
            setLastProgress(progressSteps)
        }
    }, [progressSteps])

    const displayedSteps =
        lastProgress.length > 0 ? lastProgress : progressSteps

    const errorMessage =
        taskProgress?.sub_tasks
            ?.map((subTask) => subTask.results.sub_task_error)
            .find((message) => !!message) ?? null
    const isFailed = hasFailed || taskProgress?.task_status === 'FAILED'
    const failureDetails = failureMessage ?? errorMessage

    if (isFailed) {
        return (
            <Box className={classes.failureWrapper}>
                <Box className={classes.failureCard}>
                    <Typography className={classes.failureText}>
                        We had an issue extracting document data. Please contact
                        support.
                        {failureDetails ? ` Details: ${failureDetails}` : ''}
                    </Typography>
                </Box>
            </Box>
        )
    }

    if (error || errorMessage) {
        return (
            <Box className={classes.container}>
                <Typography color="error">
                    Error loading progress: {error?.message ?? errorMessage}
                </Typography>
            </Box>
        )
    }

    if (isLoading && displayedSteps.length === 0) {
        return (
            <Box className={classes.container}>
                <Typography>Loading progress...</Typography>
            </Box>
        )
    }

    if (displayedSteps.length === 0) {
        return (
            <Box className={classes.container}>
                <Typography>Waiting to start...</Typography>
            </Box>
        )
    }

    return (
        <Box className={classes.container}>
            {taskProgress?.task_status && (
                <Typography
                    variant="body1"
                    color="textSecondary"
                    className={classes.message}
                >
                    Status: {taskProgress.task_status}
                </Typography>
            )}

            {displayedSteps.map((step, index) => {
                const percentage =
                    step.total > 0 ? (step.current / step.total) * 100 : 0

                return (
                    <Box
                        key={`${step.label}-${index}`}
                        className={classes.stepSection}
                    >
                        <Box className={classes.stepHeader}>
                            <Typography variant="h6">{step.label}</Typography>
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="flex-end"
                            >
                                <Typography variant="body1">
                                    {step.current} / {step.total}
                                </Typography>
                                {(step.elapsed !== undefined ||
                                    step.remaining !== undefined) && (
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        {step.elapsed !== undefined &&
                                            `Elapsed: ${formatTime(
                                                step.elapsed,
                                            )}`}
                                        {step.elapsed !== undefined &&
                                            step.remaining !== undefined &&
                                            ' • '}
                                        {step.remaining !== undefined &&
                                            `Remaining: ${formatTime(
                                                step.remaining,
                                            )}`}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={percentage}
                            className={classes.progressBar}
                        />
                        {step.status && (
                            <Typography variant="body2" color="textSecondary">
                                {step.status}
                            </Typography>
                        )}
                    </Box>
                )
            })}
        </Box>
    )
}
